// Curated top investor metros within the 37 states + DC where we lend.
// Each entry powers a programmatic `/dscr-loans/$state/$city` page.
export type InvestorCity = {
  slug: string;             // URL slug
  name: string;             // "Tampa"
  stateSlug: string;        // "florida"
  stateName: string;        // "Florida"
  county?: string;          // For local search intent
  centroidZip: string;      // ZIP used for HUD FMR lookup
  population: string;
  medianHomeValue: string;
  medianRent2BR: string;
  capRateBand: string;
  strengths: string[];
  watchouts: string[];
  narrative: string;
  /** Investment fundamentals (optional; renders if present). */
  economy?: string;
  employers?: string[];
  transit?: string;
  livability?: string;
  demandDrivers?: string[];
  /** Illustrative Airbnb inputs (optional; defaults derive from 2BR rent). */
  airbnbAdr?: number;
  airbnbOccupancy?: number;
};


export const investorCities: InvestorCity[] = [
  // ---------------- Colorado ----------------
  {
    slug: "denver",
    name: "Denver",
    stateSlug: "colorado",
    stateName: "Colorado",
    county: "Denver County",
    centroidZip: "80202",
    population: "716K",
    medianHomeValue: "$560,000",
    medianRent2BR: "$2,150",
    capRateBand: "4.5–6%",
    strengths: ["Long-term appreciation", "Diversified tech + healthcare economy", "Strong SFR + townhome demand"],
    watchouts: ["Tight cash flow on entry", "STR restrictions in city limits (primary residence only)"],
    narrative:
      "Denver is a growth-appreciation market where DSCR investors typically underwrite to 1.0–1.1x with long-term equity as the return driver. Suburban submarkets (Aurora, Lakewood, Arvada) offer better yield than the urban core.",
  },
  {
    slug: "colorado-springs",
    name: "Colorado Springs",
    stateSlug: "colorado",
    stateName: "Colorado",
    county: "El Paso County",
    centroidZip: "80903",
    population: "483K",
    medianHomeValue: "$445,000",
    medianRent2BR: "$1,800",
    capRateBand: "5–7%",
    strengths: ["Military-anchored rental demand (Fort Carson, USAFA)", "Better yields than Denver metro", "Landlord-favorable statutes"],
    watchouts: ["Insurance repricing after 2023 hail events", "STR heavily restricted"],
    narrative:
      "The Springs is one of Colorado's few markets where DSCR loans can pencil to 1.15x+ on long-term rentals. Military PCS cycles keep vacancy low.",
  },
  {
    slug: "pueblo",
    name: "Pueblo",
    stateSlug: "colorado",
    stateName: "Colorado",
    county: "Pueblo County",
    centroidZip: "81003",
    population: "112K",
    medianHomeValue: "$255,000",
    medianRent2BR: "$1,200",
    capRateBand: "7–9%",
    strengths: ["Highest yields on the Front Range", "Low entry price point", "Section 8 depth"],
    watchouts: ["Slower appreciation", "Insurance carrier availability tightening"],
    narrative:
      "Pueblo is the Front Range's cash-flow market. DSCR ratios routinely clear 1.3x on stabilized SFR under $300K.",
  },

  // ---------------- Florida ----------------
  {
    slug: "tampa",
    name: "Tampa",
    stateSlug: "florida",
    stateName: "Florida",
    county: "Hillsborough County",
    centroidZip: "33602",
    population: "398K",
    medianHomeValue: "$385,000",
    medianRent2BR: "$2,050",
    capRateBand: "5–7%",
    strengths: ["Population inflows", "Strong rent growth 2020–2024", "No state income tax", "Diversified financial-services economy"],
    watchouts: ["Insurance premium volatility (wind + flood)", "Hillsborough property-tax reassessments"],
    narrative:
      "Tampa is one of the deepest DSCR markets in Florida. Insurance is the top underwriting variable — always request bindable quotes before locking DSCR ratios.",
    economy: "Diversified financial-services, healthcare, defense, and port logistics — Tampa Bay added ~40K jobs in 2024–25 with unemployment near 3.4%.",
    employers: ["Raymond James Financial", "USAA (regional HQ)", "MacDill AFB / CENTCOM", "Tampa General Hospital", "Publix", "Amazon (multiple fulfillment centers)"],
    transit: "I-4, I-75, I-275 crossroads; Tampa International (TPA) ranks top-25 US airports; Port Tampa Bay is Florida's largest cargo port; expanding SunRunner BRT along the Gulf Coast.",
    livability: "Riverwalk downtown, Bayshore Boulevard, Busch Gardens, Gulf beaches within 30 min, and Tampa Bay Lightning/Buccaneers/Rays sports. Consistently ranks in top-15 US relocation destinations.",
    demandDrivers: ["Net domestic migration from Northeast + Midwest", "MacDill AFB rotation cycles", "USF + healthcare workforce", "Snowbird demand from Nov–April"],
  },
  {
    slug: "orlando",
    name: "Orlando",
    stateSlug: "florida",
    stateName: "Florida",
    county: "Orange County",
    centroidZip: "32801",
    population: "316K",
    medianHomeValue: "$395,000",
    medianRent2BR: "$2,000",
    capRateBand: "5–7%",
    strengths: ["Tourism-anchored STR demand (Kissimmee, Davenport)", "Long-term rental demand from theme-park + healthcare workforce"],
    watchouts: ["Orange County HOA restrictions on STR", "Insurance cost creep"],
    narrative:
      "Orlando splits between STR (Osceola County, most of Polk) and long-term rental submarkets. Confirm HOA STR rules on every deal — many communities have added restrictions since 2022.",
    economy: "Tourism, aerospace/defense (Lockheed, Northrop), and a fast-growing 'Medical City' cluster at Lake Nona. Orlando MSA added ~50K jobs in 2024 with ~76M annual visitors.",
    employers: ["Walt Disney World", "Universal Orlando", "AdventHealth", "Orlando Health", "Lockheed Martin", "Siemens Energy"],
    transit: "MCO — top-10 US airport by traffic; SunRail commuter rail; Brightline high-speed rail to Miami; I-4 Ultimate freeway reconstruction complete.",
    livability: "Theme parks, 100+ lakes, year-round warm weather, and rapid Lake Nona and Winter Garden development. Strong pull for families and remote workers.",
    demandDrivers: ["Theme-park + hospitality workforce (LTR)", "Vacation-rental tourism (STR)", "UCF (65K+ students)", "Puerto Rican and Latin American in-migration"],
  },

  {
    slug: "miami",
    name: "Miami",
    stateSlug: "florida",
    stateName: "Florida",
    county: "Miami-Dade County",
    centroidZip: "33131",
    population: "455K",
    medianHomeValue: "$625,000",
    medianRent2BR: "$3,100",
    capRateBand: "4–5.5%",
    strengths: ["International investor demand", "Foreign-national-friendly HOA landscape", "Strong long-term rent growth"],
    watchouts: ["Insurance is the highest DSCR variable in the country here", "Milestone inspection costs on condos"],
    narrative:
      "Miami is the top foreign-national DSCR market in the US. Condo underwriting requires reserve-study review after the 2022 Surfside legislation.",
  },
  {
    slug: "jacksonville",
    name: "Jacksonville",
    stateSlug: "florida",
    stateName: "Florida",
    county: "Duval County",
    centroidZip: "32202",
    population: "971K",
    medianHomeValue: "$305,000",
    medianRent2BR: "$1,650",
    capRateBand: "6–8%",
    strengths: ["Best cash-flow market in Florida for SFR", "Landlord-friendly", "Port + logistics economy"],
    watchouts: ["Flood-zone premiums", "Older housing stock in urban core"],
    narrative:
      "Jax is where DSCR ratios comfortably clear 1.2x+ on SFR at 75% LTV. Duval and Clay County submarkets are institutional favorites.",
  },
  {
    slug: "fort-lauderdale",
    name: "Fort Lauderdale",
    stateSlug: "florida",
    stateName: "Florida",
    county: "Broward County",
    centroidZip: "33301",
    population: "184K",
    medianHomeValue: "$525,000",
    medianRent2BR: "$2,650",
    capRateBand: "4.5–6%",
    strengths: ["Waterfront STR premium", "Corporate relocation from NYC/Chicago", "Deep small-multifamily inventory"],
    watchouts: ["Broward wind insurance", "Condo assessments post-Surfside"],
    narrative:
      "Fort Lauderdale is Miami's yield-friendlier neighbor. Duplex-to-quad DSCR files pencil best here.",
  },
  {
    slug: "cape-coral",
    name: "Cape Coral",
    stateSlug: "florida",
    stateName: "Florida",
    county: "Lee County",
    centroidZip: "33904",
    population: "216K",
    medianHomeValue: "$385,000",
    medianRent2BR: "$1,950",
    capRateBand: "5.5–7.5%",
    strengths: ["New-construction SFR at attractive basis", "Canal-access STR demand"],
    watchouts: ["Post-Ian insurance repricing", "Flood-zone AE/VE mapping"],
    narrative:
      "Cape Coral has become a national investor pick for new-build DSCR — insurance is the #1 underwriting risk since Hurricane Ian.",
  },

  // ---------------- Texas ----------------
  {
    slug: "dallas",
    name: "Dallas",
    stateSlug: "texas",
    stateName: "Texas",
    county: "Dallas County",
    centroidZip: "75201",
    population: "1.3M",
    medianHomeValue: "$395,000",
    medianRent2BR: "$1,900",
    capRateBand: "5–7%",
    strengths: ["Corporate relocations", "No state income tax", "Landlord-friendly statutes"],
    watchouts: ["Property tax rates 2.0–2.5% of value", "Insurance repricing after hail events"],
    narrative:
      "DFW property taxes are the #1 DSCR variable. Always underwrite to a fully re-assessed tax bill, not the seller's current bill.",
  },
  {
    slug: "houston",
    name: "Houston",
    stateSlug: "texas",
    stateName: "Texas",
    county: "Harris County",
    centroidZip: "77002",
    population: "2.3M",
    medianHomeValue: "$285,000",
    medianRent2BR: "$1,750",
    capRateBand: "6–8%",
    strengths: ["Energy + medical economy", "Deep SFR inventory", "Cash-flow-first market"],
    watchouts: ["Flood insurance mandatory in many zones", "MUD/PID tax overlays in newer subdivisions"],
    narrative:
      "Houston is one of the best yield markets in Texas. Confirm MUD/PID tax overlays on every subject property — they can add 1.5–2% to the effective tax rate.",
  },
  {
    slug: "san-antonio",
    name: "San Antonio",
    stateSlug: "texas",
    stateName: "Texas",
    county: "Bexar County",
    centroidZip: "78205",
    population: "1.5M",
    medianHomeValue: "$275,000",
    medianRent2BR: "$1,500",
    capRateBand: "6–8%",
    strengths: ["Military + healthcare anchor demand", "Steady rent growth", "Lower price point than DFW/Austin"],
    watchouts: ["Bexar tax reassessments", "STR permit caps in some submarkets"],
    narrative:
      "San Antonio pencils well for entry-level DSCR investors. Military BAH sets a floor on rents around Lackland and Fort Sam Houston.",
  },
  {
    slug: "fort-worth",
    name: "Fort Worth",
    stateSlug: "texas",
    stateName: "Texas",
    county: "Tarrant County",
    centroidZip: "76102",
    population: "978K",
    medianHomeValue: "$335,000",
    medianRent2BR: "$1,750",
    capRateBand: "5.5–7%",
    strengths: ["Faster growth than Dallas at lower basis", "Strong build-to-rent pipeline"],
    watchouts: ["Tarrant tax rates", "New-build appraisal risk in fringe subdivisions"],
    narrative:
      "Fort Worth is the yield-friendlier half of DFW. Watch tax bills carefully — Tarrant reassesses aggressively.",
  },

  // ---------------- Georgia ----------------
  {
    slug: "atlanta",
    name: "Atlanta",
    stateSlug: "georgia",
    stateName: "Georgia",
    county: "Fulton County",
    centroidZip: "30303",
    population: "500K",
    medianHomeValue: "$395,000",
    medianRent2BR: "$1,950",
    capRateBand: "5.5–7.5%",
    strengths: ["Institutional-SFR capital of the Southeast", "Corporate relocations", "Landlord-friendly"],
    watchouts: ["City of Atlanta STR licensing tightened", "Southside appraisal risk"],
    narrative:
      "Metro Atlanta is where institutional buyers set comp pricing. LLC vesting is required for our Georgia loans.",
  },
  {
    slug: "savannah",
    name: "Savannah",
    stateSlug: "georgia",
    stateName: "Georgia",
    county: "Chatham County",
    centroidZip: "31401",
    population: "148K",
    medianHomeValue: "$325,000",
    medianRent2BR: "$1,650",
    capRateBand: "5.5–7.5%",
    strengths: ["Historic-district STR premium", "Port + military demand"],
    watchouts: ["Chatham STR license moratoriums", "Flood zone in historic core"],
    narrative:
      "Savannah's Historic Landmark District supports strong STR ADRs when a permit conveys. Always verify STR status before writing the offer.",
  },

  // ---------------- Tennessee ----------------
  {
    slug: "nashville",
    name: "Nashville",
    stateSlug: "tennessee",
    stateName: "Tennessee",
    county: "Davidson County",
    centroidZip: "37201",
    population: "689K",
    medianHomeValue: "$455,000",
    medianRent2BR: "$2,050",
    capRateBand: "5–6.5%",
    strengths: ["No state income tax", "Music + healthcare + auto economy", "STR-friendly outside core"],
    watchouts: ["Metro STR permit caps (owner-occupied vs. non-owner)", "Compressed cap rates in urban core"],
    narrative:
      "Nashville splits sharply between owner-occupied STR (permit still available) and non-owner-occupied (largely capped). Confirm zoning before underwriting STR income.",
  },
  {
    slug: "memphis",
    name: "Memphis",
    stateSlug: "tennessee",
    stateName: "Tennessee",
    county: "Shelby County",
    centroidZip: "38103",
    population: "628K",
    medianHomeValue: "$175,000",
    medianRent2BR: "$1,250",
    capRateBand: "8–11%",
    strengths: ["Deepest cash-flow SFR market east of the Mississippi", "Logistics + healthcare jobs"],
    watchouts: ["Older housing stock — capex reserves matter", "Higher vacancy in C-class submarkets"],
    narrative:
      "Memphis is the archetypal DSCR cash-flow market. Underwrite maintenance at 8–10% and capex at 5%+, not the standard 3–5%.",
  },
  {
    slug: "knoxville",
    name: "Knoxville",
    stateSlug: "tennessee",
    stateName: "Tennessee",
    county: "Knox County",
    centroidZip: "37902",
    population: "195K",
    medianHomeValue: "$335,000",
    medianRent2BR: "$1,550",
    capRateBand: "6–8%",
    strengths: ["Smoky Mountains STR proximity", "University-anchored LTR demand"],
    watchouts: ["Sevier County STR saturation", "Steep-slope insurance"],
    narrative:
      "Knoxville metro is LTR; Sevier County (Pigeon Forge, Gatlinburg, Sevierville) is STR. Two different underwrites.",
  },

  // ---------------- Ohio ----------------
  {
    slug: "columbus",
    name: "Columbus",
    stateSlug: "ohio",
    stateName: "Ohio",
    county: "Franklin County",
    centroidZip: "43215",
    population: "907K",
    medianHomeValue: "$255,000",
    medianRent2BR: "$1,450",
    capRateBand: "6.5–8.5%",
    strengths: ["Intel + logistics job growth", "Steady appreciation + yield combination", "Deep SFR + small-multi inventory"],
    watchouts: ["Franklin tax reappraisals", "Older stock in urban core"],
    narrative:
      "Columbus is the Midwest's most balanced DSCR market — yield plus a real appreciation story from the Intel build-out in Licking County.",
  },
  {
    slug: "cleveland",
    name: "Cleveland",
    stateSlug: "ohio",
    stateName: "Ohio",
    county: "Cuyahoga County",
    centroidZip: "44113",
    population: "372K",
    medianHomeValue: "$120,000",
    medianRent2BR: "$1,150",
    capRateBand: "9–12%",
    strengths: ["Highest gross yields in our footprint", "Medical + finance anchor employers"],
    watchouts: ["Rehab-heavy stock", "Cuyahoga point-of-sale inspections", "Section 8 concentration risk in some ZIPs"],
    narrative:
      "Cleveland is a stabilized-cash-flow play. New investors underestimate capex — build reserves accordingly.",
  },
  {
    slug: "cincinnati",
    name: "Cincinnati",
    stateSlug: "ohio",
    stateName: "Ohio",
    county: "Hamilton County",
    centroidZip: "45202",
    population: "309K",
    medianHomeValue: "$230,000",
    medianRent2BR: "$1,400",
    capRateBand: "7–9%",
    strengths: ["Strong appreciation for a cash-flow market", "P&G + healthcare anchor"],
    watchouts: ["Hamilton tax reassessments", "Older housing stock in inner ring"],
    narrative:
      "Cincinnati sits between Columbus and Cleveland on the yield/appreciation curve — solid all-around DSCR market.",
  },

  // ---------------- Missouri ----------------
  {
    slug: "kansas-city",
    name: "Kansas City",
    stateSlug: "missouri",
    stateName: "Missouri",
    county: "Jackson County",
    centroidZip: "64106",
    population: "509K",
    medianHomeValue: "$245,000",
    medianRent2BR: "$1,350",
    capRateBand: "6.5–8.5%",
    strengths: ["Cheap entry basis", "Growing tech + logistics footprint", "Landlord-friendly Missouri statutes"],
    watchouts: ["KCMO rental licensing rules", "Older housing stock east of Troost"],
    narrative:
      "KCMO is a solid buy-and-hold market. Confirm the property is on the Missouri side of the state line — Kansas has different tax and licensing rules.",
  },
  {
    slug: "st-louis",
    name: "St. Louis",
    stateSlug: "missouri",
    stateName: "Missouri",
    county: "St. Louis County",
    centroidZip: "63101",
    population: "294K",
    medianHomeValue: "$205,000",
    medianRent2BR: "$1,250",
    capRateBand: "7–9%",
    strengths: ["Deep small-multi inventory", "Institutional-quality property managers"],
    watchouts: ["City vs. county jurisdictional split", "Occupancy-permit inspections"],
    narrative:
      "St. Louis has separate city and county governments — always confirm which jurisdiction the property sits in for tax and licensing.",
  },

  // ---------------- Michigan ----------------
  {
    slug: "detroit",
    name: "Detroit",
    stateSlug: "michigan",
    stateName: "Michigan",
    county: "Wayne County",
    centroidZip: "48226",
    population: "631K",
    medianHomeValue: "$85,000",
    medianRent2BR: "$1,150",
    capRateBand: "9–13%",
    strengths: ["Lowest entry basis in the country", "Downtown redevelopment tailwind"],
    watchouts: ["Wayne County tax foreclosure risk", "Property must pass rental Certificate of Compliance"],
    narrative:
      "Detroit is a stabilized-only DSCR market for us — value-add is a fix-and-flip conversation. Confirm rental compliance certificate on every file.",
  },
  {
    slug: "grand-rapids",
    name: "Grand Rapids",
    stateSlug: "michigan",
    stateName: "Michigan",
    county: "Kent County",
    centroidZip: "49503",
    population: "199K",
    medianHomeValue: "$285,000",
    medianRent2BR: "$1,550",
    capRateBand: "6–8%",
    strengths: ["Growing medical corridor", "Strong long-term rent growth"],
    watchouts: ["Kent property tax reappraisals", "Limited SFR inventory"],
    narrative:
      "Grand Rapids has been one of the Midwest's better DSCR markets for total return — appreciation plus meaningful yield.",
  },

  // ---------------- North / Mid-Atlantic ----------------
  {
    slug: "philadelphia",
    name: "Philadelphia",
    stateSlug: "pennsylvania",
    stateName: "Pennsylvania",
    county: "Philadelphia County",
    centroidZip: "19107",
    population: "1.6M",
    medianHomeValue: "$225,000",
    medianRent2BR: "$1,600",
    capRateBand: "6.5–9%",
    strengths: ["Deep row-home inventory", "University + medical anchor", "Landlord-favorable outside city core"],
    watchouts: ["Philadelphia rental license required", "Wage-tax friction for out-of-state investors"],
    narrative:
      "Philly is a top-10 US cash-flow DSCR market. Rental license and lead-safe cert are mandatory before leasing.",
  },
  {
    slug: "pittsburgh",
    name: "Pittsburgh",
    stateSlug: "pennsylvania",
    stateName: "Pennsylvania",
    county: "Allegheny County",
    centroidZip: "15222",
    population: "302K",
    medianHomeValue: "$225,000",
    medianRent2BR: "$1,450",
    capRateBand: "7–9%",
    strengths: ["Anchor universities + medical", "Cheap entry basis", "Stable rents"],
    watchouts: ["Slower appreciation", "Older stock — capex reserves needed"],
    narrative:
      "Pittsburgh is a boring, dependable cash-flow market. Underwrite conservatively on capex and it pencils reliably.",
  },
  {
    slug: "baltimore",
    name: "Baltimore",
    stateSlug: "maryland",
    stateName: "Maryland",
    county: "Baltimore City",
    centroidZip: "21202",
    population: "570K",
    medianHomeValue: "$185,000",
    medianRent2BR: "$1,450",
    capRateBand: "7–10%",
    strengths: ["Row-home yield play", "Anchor medical + federal-adjacent employment"],
    watchouts: ["Baltimore rental license + lead-safe cert required", "Ground-rent quirk on many properties"],
    narrative:
      "Baltimore is high-yield but paperwork-heavy. Every DSCR file needs rental registration and lead-safe cert verified in diligence.",
  },

  // ---------------- Northeast ----------------
  {
    slug: "boston",
    name: "Boston",
    stateSlug: "massachusetts",
    stateName: "Massachusetts",
    county: "Suffolk County",
    centroidZip: "02108",
    population: "654K",
    medianHomeValue: "$785,000",
    medianRent2BR: "$3,200",
    capRateBand: "3.5–5%",
    strengths: ["University + biotech demand", "Strong long-term appreciation"],
    watchouts: ["Tenant-favorable statutes", "STR heavily restricted"],
    narrative:
      "Boston is an appreciation-first DSCR market. Small multifamily in Dorchester, Roslindale, and near-in suburbs pencils best.",
  },
  {
    slug: "newark",
    name: "Newark",
    stateSlug: "new-jersey",
    stateName: "New Jersey",
    county: "Essex County",
    centroidZip: "07102",
    population: "305K",
    medianHomeValue: "$395,000",
    medianRent2BR: "$1,950",
    capRateBand: "6–8%",
    strengths: ["NYC-adjacent demand", "Deep 2–4 unit inventory", "Section 8 depth"],
    watchouts: ["NJ tenant protections + rent-control overlays", "Property tax rates 2.5%+"],
    narrative:
      "Newark and East Orange pencil for DSCR small-multi. Always confirm rent-control status at the municipal level.",
  },

  // ---------------- Carolinas / Southeast ----------------
  {
    slug: "charleston",
    name: "Charleston",
    stateSlug: "south-carolina",
    stateName: "South Carolina",
    county: "Charleston County",
    centroidZip: "29401",
    population: "155K",
    medianHomeValue: "$525,000",
    medianRent2BR: "$2,150",
    capRateBand: "4.5–6.5%",
    strengths: ["Tourism-driven STR (where permitted)", "Corporate relocations"],
    watchouts: ["Peninsula STR overlay restrictions", "Wind + flood insurance"],
    narrative:
      "Charleston splits between the STR-restricted peninsula and permissive Mount Pleasant / Isle of Palms submarkets — different underwrites.",
  },
  {
    slug: "columbia",
    name: "Columbia",
    stateSlug: "south-carolina",
    stateName: "South Carolina",
    county: "Richland County",
    centroidZip: "29201",
    population: "138K",
    medianHomeValue: "$225,000",
    medianRent2BR: "$1,400",
    capRateBand: "6.5–8.5%",
    strengths: ["USC + Fort Jackson anchor demand", "Cheap SFR basis"],
    watchouts: ["Older housing stock", "Insurance creep"],
    narrative:
      "Columbia is a steady LTR market with anchor demand from the university and Fort Jackson. Yields hold better than Charleston.",
  },

  // ---------------- Midwest / Rest ----------------
  {
    slug: "indianapolis",
    name: "Indianapolis",
    stateSlug: "indiana",
    stateName: "Indiana",
    county: "Marion County",
    centroidZip: "46204",
    population: "880K",
    medianHomeValue: "$235,000",
    medianRent2BR: "$1,400",
    capRateBand: "6.5–8.5%",
    strengths: ["Institutional-SFR favorite", "Landlord-friendly", "Steady rent growth"],
    watchouts: ["Marion capex on older stock", "Insurance repricing"],
    narrative:
      "Indianapolis is a top-5 institutional-SFR market — comp pricing is set by portfolio buyers, which helps refi valuations.",
  },
  {
    slug: "louisville",
    name: "Louisville",
    stateSlug: "kentucky",
    stateName: "Kentucky",
    county: "Jefferson County",
    centroidZip: "40202",
    population: "624K",
    medianHomeValue: "$225,000",
    medianRent2BR: "$1,300",
    capRateBand: "7–9%",
    strengths: ["Logistics anchor (UPS Worldport)", "Cheap SFR basis"],
    watchouts: ["Older housing stock", "Insurance rate creep"],
    narrative:
      "Louisville is a solid cash-flow market. UPS shift patterns keep vacancy low in near-airport submarkets.",
  },
  {
    slug: "birmingham",
    name: "Birmingham",
    stateSlug: "alabama",
    stateName: "Alabama",
    county: "Jefferson County",
    centroidZip: "35203",
    population: "197K",
    medianHomeValue: "$170,000",
    medianRent2BR: "$1,250",
    capRateBand: "7.5–10%",
    strengths: ["Deep cash-flow SFR inventory", "Landlord-friendly Alabama statutes"],
    watchouts: ["Older housing stock", "Neighborhood-level condition variance"],
    narrative:
      "Birmingham is one of the best sub-$200K entry markets. Vero and Trussville submarkets are institutional favorites.",
  },
  {
    slug: "huntsville",
    name: "Huntsville",
    stateSlug: "alabama",
    stateName: "Alabama",
    county: "Madison County",
    centroidZip: "35801",
    population: "225K",
    medianHomeValue: "$305,000",
    medianRent2BR: "$1,500",
    capRateBand: "5.5–7.5%",
    strengths: ["Redstone Arsenal + aerospace anchor", "Best appreciation in Alabama"],
    watchouts: ["New-build appraisal risk in outer subdivisions"],
    narrative:
      "Huntsville is Alabama's appreciation-plus-yield market. Federal + aerospace payrolls insulate rents through cycles.",
  },
  {
    slug: "oklahoma-city",
    name: "Oklahoma City",
    stateSlug: "oklahoma",
    stateName: "Oklahoma",
    county: "Oklahoma County",
    centroidZip: "73102",
    population: "695K",
    medianHomeValue: "$215,000",
    medianRent2BR: "$1,150",
    capRateBand: "7–9%",
    strengths: ["Energy + healthcare anchor", "Cheap SFR basis", "Landlord-friendly"],
    watchouts: ["Tornado / hail insurance", "OKC rental registration in some ZIPs"],
    narrative:
      "OKC delivers reliable DSCR yields. Insurance underwrites tighter than most markets — get bindable quotes early.",
  },
  {
    slug: "richmond",
    name: "Richmond",
    stateSlug: "virginia",
    stateName: "Virginia",
    county: "Richmond City",
    centroidZip: "23219",
    population: "230K",
    medianHomeValue: "$305,000",
    medianRent2BR: "$1,650",
    capRateBand: "5.5–7.5%",
    strengths: ["Federal + biotech job growth", "Strong LTR demand from VCU + medical"],
    watchouts: ["City rental license required", "Older stock in Fan / Church Hill"],
    narrative:
      "Richmond is a stable appreciation-plus-yield market. Chesterfield and Henrico suburbs pencil better than the city.",
  },

  // ---------------- Pacific / Mountain ----------------
  {
    slug: "seattle",
    name: "Seattle",
    stateSlug: "washington",
    stateName: "Washington",
    county: "King County",
    centroidZip: "98101",
    population: "749K",
    medianHomeValue: "$855,000",
    medianRent2BR: "$2,650",
    capRateBand: "3.5–5%",
    strengths: ["Tech-anchored long-term appreciation", "No state income tax"],
    watchouts: ["Tenant-favorable Seattle statutes", "STR heavily restricted"],
    narrative:
      "Seattle is an appreciation-first DSCR market. Suburban Snohomish and Pierce County submarkets pencil better than King County.",
  },
  {
    slug: "spokane",
    name: "Spokane",
    stateSlug: "washington",
    stateName: "Washington",
    county: "Spokane County",
    centroidZip: "99201",
    population: "230K",
    medianHomeValue: "$375,000",
    medianRent2BR: "$1,550",
    capRateBand: "5.5–7.5%",
    strengths: ["Better yields than Seattle metro", "Growing healthcare + higher-ed base"],
    watchouts: ["Insurance creep", "Older stock in South Hill"],
    narrative:
      "Spokane is Washington's yield-friendlier metro. LTR SFR pencils to 1.15x+ DSCR at 75% LTV.",
  },

  // ---------------- Alaska ----------------
  {
    slug: "anchorage", name: "Anchorage", stateSlug: "alaska", stateName: "Alaska",
    county: "Anchorage Municipality", centroidZip: "99501",
    population: "288K", medianHomeValue: "$385,000", medianRent2BR: "$1,700", capRateBand: "5.5–7%",
    strengths: ["Federal + military anchor demand", "Limited new supply keeps vacancy low", "Higher rents than most Mountain-West metros"],
    watchouts: ["Insurance and heating cost volatility", "Slow appreciation over long horizons"],
    narrative: "Anchorage is Alaska's dominant rental market. LTR SFR pencils to ~1.10–1.20x DSCR at 75% LTV; STR demand spikes each summer.",
  },
  {
    slug: "fairbanks", name: "Fairbanks", stateSlug: "alaska", stateName: "Alaska",
    county: "Fairbanks North Star Borough", centroidZip: "99701",
    population: "31K", medianHomeValue: "$285,000", medianRent2BR: "$1,450", capRateBand: "7–9%",
    strengths: ["Highest cash-flow city in Alaska", "Ft. Wainwright + UAF anchor demand"],
    watchouts: ["Extreme winters raise vacancy in older stock", "Small buyer pool at exit"],
    narrative: "Fairbanks is Alaska's yield play. DSCR clears 1.25x+ on stabilized SFR with military-anchored occupancy.",
  },

  // ---------------- Arkansas ----------------
  {
    slug: "little-rock", name: "Little Rock", stateSlug: "arkansas", stateName: "Arkansas",
    county: "Pulaski County", centroidZip: "72201",
    population: "202K", medianHomeValue: "$205,000", medianRent2BR: "$1,275", capRateBand: "7.5–9.5%",
    strengths: ["Low entry price", "Deep Section 8 demand", "Landlord-friendly courts"],
    watchouts: ["Neighborhood-by-neighborhood underwriting matters", "Older housing stock"],
    narrative: "Little Rock is a steady cash-flow market. Sub-$250K SFR routinely underwrites to 1.30x+ DSCR.",
  },
  {
    slug: "fayetteville", name: "Fayetteville", stateSlug: "arkansas", stateName: "Arkansas",
    county: "Washington County", centroidZip: "72701",
    population: "95K", medianHomeValue: "$345,000", medianRent2BR: "$1,600", capRateBand: "6–8%",
    strengths: ["U. of Arkansas + Walmart/Tyson ecosystem", "Fast-appreciating NW Arkansas corridor"],
    watchouts: ["Student turnover in near-campus zips", "Rising insurance"],
    narrative: "Fayetteville is Arkansas's growth market — the NWA corridor is one of the fastest-appreciating in the South.",
  },
  {
    slug: "bentonville", name: "Bentonville", stateSlug: "arkansas", stateName: "Arkansas",
    county: "Benton County", centroidZip: "72712",
    population: "60K", medianHomeValue: "$465,000", medianRent2BR: "$1,850", capRateBand: "5–7%",
    strengths: ["Walmart HQ magnet for high-income renters", "Premium school districts"],
    watchouts: ["Priciest entry in the region", "STR competition rising"],
    narrative: "Bentonville is an appreciation play with a wealthy, professional renter base tied to Walmart and vendor HQs.",
  },

  // ---------------- Connecticut ----------------
  {
    slug: "hartford", name: "Hartford", stateSlug: "connecticut", stateName: "Connecticut",
    county: "Hartford County", centroidZip: "06103",
    population: "121K", medianHomeValue: "$225,000", medianRent2BR: "$1,700", capRateBand: "7–9%",
    strengths: ["Insurance-industry job anchor", "Strongest yields in New England"],
    watchouts: ["Tenant-friendly courts", "Property tax mill rates"],
    narrative: "Hartford is New England's highest-yield metro. Multifamily and SFR sub-$300K routinely clear 1.25x DSCR.",
  },
  {
    slug: "new-haven", name: "New Haven", stateSlug: "connecticut", stateName: "Connecticut",
    county: "New Haven County", centroidZip: "06510",
    population: "135K", medianHomeValue: "$275,000", medianRent2BR: "$1,900", capRateBand: "6–8%",
    strengths: ["Yale + Yale-New Haven Health anchor", "Deep tenant demand year-round"],
    watchouts: ["Higher mill rates than the shoreline", "Older stock needs capex"],
    narrative: "New Haven pairs stable university-driven demand with strong rents. DSCR files near Yale campus underwrite well.",
  },
  {
    slug: "bridgeport", name: "Bridgeport", stateSlug: "connecticut", stateName: "Connecticut",
    county: "Fairfield County", centroidZip: "06604",
    population: "148K", medianHomeValue: "$285,000", medianRent2BR: "$1,950", capRateBand: "6–8%",
    strengths: ["Metro-North commuter demand to NYC", "Section 8 + workforce housing depth"],
    watchouts: ["Tenant-friendly statute set", "Older brick stock"],
    narrative: "Bridgeport is a workforce-housing yield play with NYC-commuter tailwinds.",
  },

  // ---------------- District of Columbia ----------------
  {
    slug: "washington", name: "Washington", stateSlug: "district-of-columbia", stateName: "District of Columbia",
    county: "District of Columbia", centroidZip: "20001",
    population: "679K", medianHomeValue: "$625,000", medianRent2BR: "$2,700", capRateBand: "4–5.5%",
    strengths: ["Federal-employment anchored demand", "Deep long-term tenant base"],
    watchouts: ["TOPA (Tenant Opportunity to Purchase Act) affects sales", "Strong tenant protections"],
    narrative: "DC is an appreciation-first market. Underwrite to 1.0–1.10x DSCR; equity growth and stability drive returns.",
  },

  // ---------------- Delaware ----------------
  {
    slug: "wilmington", name: "Wilmington", stateSlug: "delaware", stateName: "Delaware",
    county: "New Castle County", centroidZip: "19801",
    population: "70K", medianHomeValue: "$255,000", medianRent2BR: "$1,600", capRateBand: "6–8%",
    strengths: ["Corporate-services HQ base", "Amtrak corridor access"],
    watchouts: ["Neighborhood-level underwriting matters", "Aging stock"],
    narrative: "Wilmington offers workforce-housing yield with corporate-tenant depth from banking and legal services.",
  },
  {
    slug: "dover", name: "Dover", stateSlug: "delaware", stateName: "Delaware",
    county: "Kent County", centroidZip: "19901",
    population: "40K", medianHomeValue: "$285,000", medianRent2BR: "$1,550", capRateBand: "6–8%",
    strengths: ["Dover AFB anchor demand", "State-capital employment base"],
    watchouts: ["Smaller buyer pool at exit", "Insurance creep"],
    narrative: "Dover is a military + state-government market with steady rental demand.",
  },

  // ---------------- Hawaii ----------------
  {
    slug: "honolulu", name: "Honolulu", stateSlug: "hawaii", stateName: "Hawaii",
    county: "Honolulu County", centroidZip: "96813",
    population: "343K", medianHomeValue: "$860,000", medianRent2BR: "$2,900", capRateBand: "3.5–5%",
    strengths: ["Chronic supply shortage", "Long-term appreciation track record"],
    watchouts: ["STR crackdowns statewide", "Insurance and HOA cost"],
    narrative: "Honolulu is an appreciation-first market with long-term-rent fallback DSCR underwriting after 2024 STR rule changes.",
  },
  {
    slug: "kailua-kona", name: "Kailua-Kona", stateSlug: "hawaii", stateName: "Hawaii",
    county: "Hawaii County", centroidZip: "96740",
    population: "23K", medianHomeValue: "$780,000", medianRent2BR: "$2,600", capRateBand: "4–6%",
    strengths: ["Established STR-legal zones", "Tourism-driven ADR"],
    watchouts: ["Volcano/lava-zone insurance", "County STR permitting"],
    narrative: "Kailua-Kona is one of Hawaii's few STR-friendly submarkets; DSCR underwrites on long-term-rent floor with STR upside.",
  },

  // ---------------- Illinois ----------------
  {
    slug: "chicago", name: "Chicago", stateSlug: "illinois", stateName: "Illinois",
    county: "Cook County", centroidZip: "60601",
    population: "2.66M", medianHomeValue: "$310,000", medianRent2BR: "$2,150", capRateBand: "5–8%",
    strengths: ["Deep multifamily + SFR inventory", "Neighborhood cash-flow variety", "Section 8 depth on south/west sides"],
    watchouts: ["Cook County property taxes are the top DSCR variable", "Tenant-favorable ordinances"],
    narrative: "Chicago offers some of the country's best cash-flow-per-dollar in specific submarkets. Taxes drive underwriting — model to actual post-sale reassessment.",
  },
  {
    slug: "aurora", name: "Aurora", stateSlug: "illinois", stateName: "Illinois",
    county: "Kane County", centroidZip: "60505",
    population: "180K", medianHomeValue: "$285,000", medianRent2BR: "$1,750", capRateBand: "6–8%",
    strengths: ["Better landlord environment than Chicago proper", "Metra commuter access"],
    watchouts: ["School-district-driven pricing variance"],
    narrative: "Aurora is a workforce-housing yield market with Chicago-commuter demand and cleaner underwriting than Cook County.",
  },
  {
    slug: "rockford", name: "Rockford", stateSlug: "illinois", stateName: "Illinois",
    county: "Winnebago County", centroidZip: "61101",
    population: "146K", medianHomeValue: "$155,000", medianRent2BR: "$1,150", capRateBand: "8–11%",
    strengths: ["Highest yields in Illinois", "Section 8 depth", "Low entry price"],
    watchouts: ["Slower appreciation", "Older stock capex"],
    narrative: "Rockford is Illinois's cash-flow market — sub-$200K SFR frequently clears 1.35x+ DSCR.",
  },

  // ---------------- Kansas ----------------
  {
    slug: "wichita", name: "Wichita", stateSlug: "kansas", stateName: "Kansas",
    county: "Sedgwick County", centroidZip: "67202",
    population: "397K", medianHomeValue: "$195,000", medianRent2BR: "$1,150", capRateBand: "8–10%",
    strengths: ["Highest-yield mid-sized metro in the Plains", "Aerospace employer base (Spirit, Textron)"],
    watchouts: ["Insurance creep after hail seasons", "Slow appreciation"],
    narrative: "Wichita is the Plains' cash-flow capital. Stabilized SFR routinely clears 1.30x+ DSCR.",
  },
  {
    slug: "overland-park", name: "Overland Park", stateSlug: "kansas", stateName: "Kansas",
    county: "Johnson County", centroidZip: "66212",
    population: "197K", medianHomeValue: "$425,000", medianRent2BR: "$1,850", capRateBand: "5–7%",
    strengths: ["Premium Johnson County schools", "High-income renter base"],
    watchouts: ["Tighter cap rates", "Newer stock premium"],
    narrative: "Overland Park is the Kansas-side appreciation play adjacent to Kansas City — long-hold equity growth.",
  },
  {
    slug: "topeka", name: "Topeka", stateSlug: "kansas", stateName: "Kansas",
    county: "Shawnee County", centroidZip: "66603",
    population: "126K", medianHomeValue: "$165,000", medianRent2BR: "$1,050", capRateBand: "8–10%",
    strengths: ["State-capital employment base", "Very low entry price"],
    watchouts: ["Small exit buyer pool", "Older housing stock"],
    narrative: "Topeka is a Section 8 + workforce-housing cash-flow market with strong DSCR ratios on modest capital outlay.",
  },

  // ---------------- Louisiana ----------------
  {
    slug: "new-orleans", name: "New Orleans", stateSlug: "louisiana", stateName: "Louisiana",
    county: "Orleans Parish", centroidZip: "70112",
    population: "376K", medianHomeValue: "$260,000", medianRent2BR: "$1,700", capRateBand: "6–8%",
    strengths: ["Tourism-anchored STR demand where permitted", "Deep long-term rental base"],
    watchouts: ["STR permits owner-occupied only in most zones", "Flood insurance is required underwriting"],
    narrative: "New Orleans underwrites on long-term-rent DSCR; STR is a bonus only in a handful of commercial-zoned corridors.",
  },
  {
    slug: "baton-rouge", name: "Baton Rouge", stateSlug: "louisiana", stateName: "Louisiana",
    county: "East Baton Rouge Parish", centroidZip: "70802",
    population: "222K", medianHomeValue: "$225,000", medianRent2BR: "$1,300", capRateBand: "7–9%",
    strengths: ["LSU + state-capital employment", "Landlord-friendly courts"],
    watchouts: ["Flood insurance zones", "Neighborhood-level underwriting"],
    narrative: "Baton Rouge is a steady cash-flow market with LSU-driven demand and clean landlord statutes.",
  },
  {
    slug: "shreveport", name: "Shreveport", stateSlug: "louisiana", stateName: "Louisiana",
    county: "Caddo Parish", centroidZip: "71101",
    population: "184K", medianHomeValue: "$155,000", medianRent2BR: "$1,050", capRateBand: "9–11%",
    strengths: ["Highest yields in Louisiana", "Section 8 depth"],
    watchouts: ["Slow appreciation", "Older stock"],
    narrative: "Shreveport is Louisiana's yield market — sub-$200K SFR frequently clears 1.40x DSCR.",
  },

  // ---------------- Maine ----------------
  {
    slug: "portland", name: "Portland", stateSlug: "maine", stateName: "Maine",
    county: "Cumberland County", centroidZip: "04101",
    population: "68K", medianHomeValue: "$525,000", medianRent2BR: "$2,300", capRateBand: "5–7%",
    strengths: ["Coastal STR premium (where permitted)", "Healthcare + higher-ed anchor"],
    watchouts: ["STR caps in Portland proper", "Older stock capex"],
    narrative: "Portland's STR ordinances tightened; DSCR underwrites on long-term-rent floor with seasonal STR upside.",
  },
  {
    slug: "bangor", name: "Bangor", stateSlug: "maine", stateName: "Maine",
    county: "Penobscot County", centroidZip: "04401",
    population: "31K", medianHomeValue: "$225,000", medianRent2BR: "$1,300", capRateBand: "7–9%",
    strengths: ["Northern Maine healthcare hub", "Better yields than coastal Maine"],
    watchouts: ["Small buyer pool", "Heating cost pass-throughs"],
    narrative: "Bangor is Maine's yield market anchored by Northern Light Health and the University of Maine system.",
  },

  // ---------------- Mississippi ----------------
  {
    slug: "jackson", name: "Jackson", stateSlug: "mississippi", stateName: "Mississippi",
    county: "Hinds County", centroidZip: "39201",
    population: "146K", medianHomeValue: "$110,000", medianRent2BR: "$1,050", capRateBand: "9–12%",
    strengths: ["Very low entry price", "State-capital + medical employment"],
    watchouts: ["Neighborhood-level underwriting is critical", "Older stock"],
    narrative: "Jackson is one of the highest-DSCR markets in the country for risk-tolerant investors; strict zip-by-zip diligence required.",
  },
  {
    slug: "gulfport", name: "Gulfport", stateSlug: "mississippi", stateName: "Mississippi",
    county: "Harrison County", centroidZip: "39501",
    population: "72K", medianHomeValue: "$205,000", medianRent2BR: "$1,400", capRateBand: "7–9%",
    strengths: ["Coastal STR demand", "Keesler AFB anchor"],
    watchouts: ["Windstorm insurance", "Flood zones"],
    narrative: "Gulfport pairs military-anchored long-term rent with seasonal STR premium along the Coast.",
  },

  // ---------------- New Hampshire ----------------
  {
    slug: "manchester", name: "Manchester", stateSlug: "new-hampshire", stateName: "New Hampshire",
    county: "Hillsborough County", centroidZip: "03101",
    population: "115K", medianHomeValue: "$395,000", medianRent2BR: "$1,900", capRateBand: "5.5–7.5%",
    strengths: ["Boston-commuter tailwind", "No state income tax"],
    watchouts: ["Higher property taxes", "Older stock"],
    narrative: "Manchester is southern NH's yield-friendlier market with Boston commuter demand.",
  },
  {
    slug: "nashua", name: "Nashua", stateSlug: "new-hampshire", stateName: "New Hampshire",
    county: "Hillsborough County", centroidZip: "03060",
    population: "91K", medianHomeValue: "$465,000", medianRent2BR: "$2,050", capRateBand: "5–7%",
    strengths: ["MA-border shopping/tax arbitrage renter base", "Strong school districts"],
    watchouts: ["Property tax rate", "Tight cap rates"],
    narrative: "Nashua is an appreciation-first NH market pulling MA-border renters seeking no state income tax.",
  },

  // ---------------- New Mexico ----------------
  {
    slug: "albuquerque", name: "Albuquerque", stateSlug: "new-mexico", stateName: "New Mexico",
    county: "Bernalillo County", centroidZip: "87102",
    population: "560K", medianHomeValue: "$310,000", medianRent2BR: "$1,450", capRateBand: "6–8%",
    strengths: ["Kirtland AFB + Sandia Labs anchor", "Steady LTR demand"],
    watchouts: ["Neighborhood variance", "Insurance creep"],
    narrative: "Albuquerque is New Mexico's dominant rental market — federal-employment anchored with steady mid-single-digit yields.",
  },
  {
    slug: "las-cruces", name: "Las Cruces", stateSlug: "new-mexico", stateName: "New Mexico",
    county: "Doña Ana County", centroidZip: "88001",
    population: "112K", medianHomeValue: "$275,000", medianRent2BR: "$1,250", capRateBand: "7–9%",
    strengths: ["NMSU + White Sands Missile Range demand", "Better yields than Albuquerque"],
    watchouts: ["Smaller exit buyer pool"],
    narrative: "Las Cruces is a university + federal cash-flow market with clean DSCR ratios on modest capital.",
  },

  // ---------------- New York ----------------
  {
    slug: "buffalo", name: "Buffalo", stateSlug: "new-york", stateName: "New York",
    county: "Erie County", centroidZip: "14202",
    population: "276K", medianHomeValue: "$210,000", medianRent2BR: "$1,350", capRateBand: "8–10%",
    strengths: ["Best cash-flow major metro in NY", "Healthcare + higher-ed anchor"],
    watchouts: ["Tenant-favorable statewide statutes", "Older stock, heating capex"],
    narrative: "Buffalo is upstate NY's cash-flow leader — sub-$250K SFR frequently clears 1.30x+ DSCR.",
  },
  {
    slug: "rochester", name: "Rochester", stateSlug: "new-york", stateName: "New York",
    county: "Monroe County", centroidZip: "14604",
    population: "211K", medianHomeValue: "$185,000", medianRent2BR: "$1,300", capRateBand: "8–10%",
    strengths: ["U. of Rochester + Strong Memorial employment", "Very low entry price"],
    watchouts: ["Property tax rate", "Older stock"],
    narrative: "Rochester is a healthcare + higher-ed anchored yield market with clean DSCR on entry-level SFR.",
  },
  {
    slug: "syracuse", name: "Syracuse", stateSlug: "new-york", stateName: "New York",
    county: "Onondaga County", centroidZip: "13202",
    population: "148K", medianHomeValue: "$175,000", medianRent2BR: "$1,300", capRateBand: "8–10%",
    strengths: ["Micron $100B fab under construction — long-term demand tailwind", "Syracuse U. anchor"],
    watchouts: ["Winter capex", "Statewide tenant protections"],
    narrative: "Syracuse is one of the strongest growth-plus-yield stories in the Northeast on the back of Micron's Clay fab.",
  },

  // ---------------- Rhode Island ----------------
  {
    slug: "providence", name: "Providence", stateSlug: "rhode-island", stateName: "Rhode Island",
    county: "Providence County", centroidZip: "02903",
    population: "190K", medianHomeValue: "$395,000", medianRent2BR: "$2,100", capRateBand: "5.5–7.5%",
    strengths: ["Brown + RISD + Lifespan anchor", "Boston-adjacent overflow demand"],
    watchouts: ["Older triple-decker stock capex", "Tenant-favorable courts"],
    narrative: "Providence is Rhode Island's dominant rental market with deep student + healthcare demand.",
  },
  {
    slug: "warwick", name: "Warwick", stateSlug: "rhode-island", stateName: "Rhode Island",
    county: "Kent County", centroidZip: "02886",
    population: "83K", medianHomeValue: "$395,000", medianRent2BR: "$1,950", capRateBand: "5.5–7.5%",
    strengths: ["Airport + commercial employment", "Suburban SFR depth"],
    watchouts: ["Older stock", "Tight cap rates"],
    narrative: "Warwick is Providence's suburban SFR complement with steady long-term-rent DSCR files.",
  },

  // ---------------- West Virginia ----------------
  {
    slug: "charleston", name: "Charleston", stateSlug: "west-virginia", stateName: "West Virginia",
    county: "Kanawha County", centroidZip: "25301",
    population: "48K", medianHomeValue: "$155,000", medianRent2BR: "$1,050", capRateBand: "8–10%",
    strengths: ["State-capital employment base", "Very low entry price"],
    watchouts: ["Small exit buyer pool", "Slow appreciation"],
    narrative: "Charleston is a Section 8 + workforce cash-flow market with strong DSCR ratios on modest capital.",
  },
  {
    slug: "huntington", name: "Huntington", stateSlug: "west-virginia", stateName: "West Virginia",
    county: "Cabell County", centroidZip: "25701",
    population: "45K", medianHomeValue: "$135,000", medianRent2BR: "$950", capRateBand: "9–11%",
    strengths: ["Marshall U. anchor demand", "Very high headline yields"],
    watchouts: ["Older stock capex", "Population trend"],
    narrative: "Huntington is a university + healthcare cash-flow market with headline DSCR ratios near 1.40x.",
  },
  {
    slug: "morgantown", name: "Morgantown", stateSlug: "west-virginia", stateName: "West Virginia",
    county: "Monongalia County", centroidZip: "26505",
    population: "30K", medianHomeValue: "$265,000", medianRent2BR: "$1,300", capRateBand: "7–9%",
    strengths: ["WVU + WVU Medicine anchor", "Fastest-growing WV metro"],
    watchouts: ["Student turnover in near-campus zips"],
    narrative: "Morgantown is West Virginia's growth-and-yield market anchored by WVU.",
  },

  // ---------------- Wisconsin ----------------
  {
    slug: "milwaukee", name: "Milwaukee", stateSlug: "wisconsin", stateName: "Wisconsin",
    county: "Milwaukee County", centroidZip: "53202",
    population: "561K", medianHomeValue: "$185,000", medianRent2BR: "$1,450", capRateBand: "7–9%",
    strengths: ["Deep duplex/triplex inventory", "Best cash-flow major metro in Wisconsin"],
    watchouts: ["Older brick + wood-frame capex", "Neighborhood-level underwriting"],
    narrative: "Milwaukee is a duplex-heavy cash-flow market with strong DSCR ratios on entry-level small multifamily.",
  },
  {
    slug: "madison", name: "Madison", stateSlug: "wisconsin", stateName: "Wisconsin",
    county: "Dane County", centroidZip: "53703",
    population: "272K", medianHomeValue: "$425,000", medianRent2BR: "$1,900", capRateBand: "5–7%",
    strengths: ["UW-Madison + state-capital employment", "Chronic housing shortage"],
    watchouts: ["Tight cap rates", "New-supply competition"],
    narrative: "Madison is an appreciation-first market with a professional-and-student renter base and chronic supply shortage.",
  },
  {
    slug: "green-bay", name: "Green Bay", stateSlug: "wisconsin", stateName: "Wisconsin",
    county: "Brown County", centroidZip: "54301",
    population: "107K", medianHomeValue: "$245,000", medianRent2BR: "$1,300", capRateBand: "6.5–8.5%",
    strengths: ["Diversified paper/logistics/food economy", "Low vacancy"],
    watchouts: ["Winter heating pass-throughs", "Smaller buyer pool"],
    narrative: "Green Bay is a steady, low-volatility cash-flow market anchored by a diversified employer base.",
  },

  // ---------------- Wyoming ----------------
  {
    slug: "cheyenne", name: "Cheyenne", stateSlug: "wyoming", stateName: "Wyoming",
    county: "Laramie County", centroidZip: "82001",
    population: "65K", medianHomeValue: "$335,000", medianRent2BR: "$1,450", capRateBand: "6–8%",
    strengths: ["FE Warren AFB + state-capital anchor", "No state income tax"],
    watchouts: ["Small exit buyer pool", "Wind/hail insurance"],
    narrative: "Cheyenne is Wyoming's largest rental market — federal + state employment underpins steady LTR demand.",
  },
  {
    slug: "casper", name: "Casper", stateSlug: "wyoming", stateName: "Wyoming",
    county: "Natrona County", centroidZip: "82601",
    population: "58K", medianHomeValue: "$285,000", medianRent2BR: "$1,300", capRateBand: "7–9%",
    strengths: ["Better yields than Cheyenne", "Energy-sector demand cycles"],
    watchouts: ["Energy-price-driven vacancy volatility"],
    narrative: "Casper is Wyoming's yield-friendlier metro with energy-linked demand cycles — underwrite conservatively on vacancy.",
  },

  // ---------------- Additional depth for thin states ----------------
  // Indiana
  {
    slug: "fort-wayne", name: "Fort Wayne", stateSlug: "indiana", stateName: "Indiana",
    county: "Allen County", centroidZip: "46802",
    population: "270K", medianHomeValue: "$195,000", medianRent2BR: "$1,200", capRateBand: "7.5–9.5%",
    strengths: ["Diversified NE Indiana economy", "Landlord-friendly courts", "Very low entry price"],
    watchouts: ["Older stock in urban core"],
    narrative: "Fort Wayne is a top-tier Midwest cash-flow market — stabilized SFR frequently clears 1.30x DSCR.",
  },
  // Kentucky
  {
    slug: "lexington", name: "Lexington", stateSlug: "kentucky", stateName: "Kentucky",
    county: "Fayette County", centroidZip: "40507",
    population: "322K", medianHomeValue: "$285,000", medianRent2BR: "$1,400", capRateBand: "6.5–8.5%",
    strengths: ["U. of Kentucky anchor", "Steady healthcare + equine economy"],
    watchouts: ["Student turnover in near-campus zips"],
    narrative: "Lexington is Kentucky's growth-and-yield market anchored by UK and a diversified professional base.",
  },
];


// Filter placeholder / unlicensed-state entries defensively.
export const publishedInvestorCities: InvestorCity[] = investorCities.filter(
  (c) => c.slug && c.stateSlug && c.narrative,
);

export function findCity(stateSlug: string, citySlug: string): InvestorCity | undefined {
  return publishedInvestorCities.find(
    (c) => c.stateSlug === stateSlug && c.slug === citySlug,
  );
}
